import React, { useState, useEffect } from 'react';
import { MainButton } from '../../portal/components/aeroglass/MainButton';

interface NavigationTutorialProps {
  onComplete: () => void;
}

type TutorialStep = 'INTRO' | 'TEST_UP' | 'TEST_DOWN' | 'TEST_SIDE' | 'TEST_CLICK' | 'COMPLETE';

export const NavigationTutorial: React.FC<NavigationTutorialProps> = ({ onComplete }) => {
  const [step, setStep] = useState<TutorialStep>('INTRO');
  const [bgOffset, setBgOffset] = useState(0);
  const [bgSideOffset, setBgSideOffset] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Reset visual offsets after animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setBgOffset(0);
      setBgSideOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [step, bgOffset, bgSideOffset]);

  const handleStart = () => {
    if (step === 'INTRO') {
      setStep('TEST_UP');
    }
  };

  const handleNavigate = (direction: 'up' | 'down' | 'left' | 'right') => {
    // 1. Swipe UP
    if (step === 'TEST_UP' && direction === 'up') {
      setBgOffset(-100); // Simulate scroll down
      setStep('TEST_DOWN');
    }
    // 2. Swipe DOWN
    else if (step === 'TEST_DOWN' && direction === 'down') {
      setBgOffset(100); // Simulate scroll up/refresh
      setStep('TEST_SIDE');
    }
    // 3. Swipe SIDES
    else if (step === 'TEST_SIDE' && (direction === 'left' || direction === 'right')) {
      setBgSideOffset(direction === 'left' ? -50 : 50);
      setStep('TEST_CLICK');
    }
  };

  const handleClick = () => {
    if (step === 'INTRO') {
      handleStart();
    } else if (step === 'TEST_CLICK') {
      setIsMenuOpen(true);
      setStep('COMPLETE');
      setTimeout(() => {
        onComplete();
      }, 2000);
    }
  };

  // Dynamic Instructions
  const getInstruction = () => {
    switch (step) {
      case 'INTRO': return { title: 'Vamos Começar', sub: 'Toque para iniciar o tutorial' };
      case 'TEST_UP': return { title: 'Deslize para Cima', sub: 'Para ver mais conteúdo (Scroll)' };
      case 'TEST_DOWN': return { title: 'Deslize para Baixo', sub: 'Para atualizar ou voltar' };
      case 'TEST_SIDE': return { title: 'Deslize para o Lado', sub: 'Para navegar entre abas' };
      case 'TEST_CLICK': return { title: 'Acesse o Menu', sub: 'Toque no centro para abrir' };
      case 'COMPLETE': return { title: 'Tudo Pronto!', sub: 'Você está no comando.' };
    }
  };

  const instruction = getInstruction();

  return (
    <div className="flex flex-col items-center text-center animate-fade-in w-full max-w-sm mx-auto">
      
      {/* Instructions */}
      <div className="mb-12 space-y-4 min-h-[100px] transition-all duration-300">
        <h1 key={step} className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70 tracking-tight animate-fade-in-up">
          {instruction.title}
        </h1>
        <p className="text-lg text-white/60 font-light mx-auto leading-relaxed animate-fade-in-up delay-75">
          {instruction.sub}
        </p>
      </div>

      <div className="relative h-[240px] w-full flex items-center justify-center">
        
        {/* Interactive Background Feed */}
        <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none -z-10 mask-gradient-to-b rounded-2xl border border-white/5 bg-black/20">
           <div 
             className="space-y-3 p-4 transition-transform duration-500 ease-out"
             style={{ transform: `translate(${bgSideOffset}px, ${bgOffset}px)` }}
           >
               {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex gap-2">
                      <div className="w-10 h-10 rounded-lg bg-white/30" />
                      <div className="flex-1 space-y-2">
                          <div className="h-3 w-3/4 rounded bg-white/30" />
                          <div className="h-2 w-1/2 rounded bg-white/20" />
                      </div>
                  </div>
               ))}
           </div>
        </div>

        {/* Highlight Ring */}
        <div className={`absolute inset-0 bg-brand-blue-DEFAULT/20 blur-[60px] rounded-full transition-opacity duration-500 pointer-events-none ${step !== 'COMPLETE' ? 'opacity-100 animate-pulse-slow' : 'opacity-0'}`} />
        
        {/* Main Interface */}
        <div className={`transform transition-all duration-500 ${isMenuOpen ? 'scale-100' : 'scale-125'}`}>
           <MainButton 
              isOpen={isMenuOpen} 
              onClick={handleClick}
              onNavigate={handleNavigate}
           />
        </div>

        {/* Visual Hints (Arrows) */}
        <div className="absolute inset-0 pointer-events-none">
            
            {/* HINT: UP */}
            {step === 'TEST_UP' && (
                <div className="absolute top-[-30px] left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
                    <span className="material-symbols-outlined text-brand-gold text-4xl drop-shadow-glow">keyboard_arrow_up</span>
                    <span className="text-[10px] font-bold text-brand-gold uppercase tracking-wider">Swipe Up</span>
                </div>
            )}

            {/* HINT: DOWN */}
            {step === 'TEST_DOWN' && (
                <div className="absolute bottom-[-30px] left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
                    <span className="text-[10px] font-bold text-brand-gold uppercase tracking-wider">Swipe Down</span>
                    <span className="material-symbols-outlined text-brand-gold text-4xl drop-shadow-glow">keyboard_arrow_down</span>
                </div>
            )}

            {/* HINT: SIDES */}
            {step === 'TEST_SIDE' && (
                <>
                    <div className="absolute left-[-20px] top-1/2 -translate-y-1/2 flex flex-col items-center animate-pulse">
                        <span className="material-symbols-outlined text-white text-4xl drop-shadow-md">keyboard_arrow_left</span>
                    </div>
                    <div className="absolute right-[-20px] top-1/2 -translate-y-1/2 flex flex-col items-center animate-pulse">
                        <span className="material-symbols-outlined text-white text-4xl drop-shadow-md">keyboard_arrow_right</span>
                    </div>
                </>
            )}

            {/* HINT: CLICK */}
            {step === 'TEST_CLICK' && (
                 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-[80px] h-[80px] rounded-full border-2 border-brand-gold animate-ping opacity-50" />
                 </div>
            )}
        </div>
      </div>
      
      {/* Progress Dots */}
      <div className="flex gap-2 mt-8">
          {['INTRO', 'TEST_UP', 'TEST_DOWN', 'TEST_SIDE', 'TEST_CLICK', 'COMPLETE'].map((s, i) => (
              <div 
                key={s} 
                className={`h-1.5 rounded-full transition-all duration-300 ${
                    s === step ? 'w-8 bg-brand-gold' : 
                    ['INTRO', 'TEST_UP', 'TEST_DOWN', 'TEST_SIDE', 'TEST_CLICK', 'COMPLETE'].indexOf(step) > i ? 'w-1.5 bg-brand-gold/50' : 'w-1.5 bg-white/10'
                }`} 
              />
          ))}
      </div>
    </div>
  );
};
